import { useMemo, useState } from 'react';
import { Bed, CalendarCheck, DoorOpen, HouseLine, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

import {
  useAddBoardingStayUpdate,
  useBindBoardingStayQuote,
  useBoardingCustomerReport,
  useBoardingStays,
  useBoardingUnits,
  useCreateBoardingCashierOrder,
  useEnsureBoardingStay,
  useUpdateBoardingUnitStatus,
  useUpdateBoardingStay,
  useUpsertBoardingUnit,
  type BoardingStayStatus,
  type BoardingCustomerReport,
  type BoardingStayUpdateEvent,
  type BoardingNightlyQuotePreview,
  type BoardingUpdateMood,
  type BoardingUpdateVisibility,
  type BoardingUnitStatus,
  type BoardingUnitType,
} from '@/features/boarding';
import { useCurrentBranch } from '@/shared/context/TenantContext';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { StatusBadge, type StatusVariant } from '@/shared/ui/StatusBadge';
import { formatDateTime } from '@/shared/utils/formatters';

const STATUS_LABELS: Record<BoardingStayStatus | 'not_started', string> = {
  not_started: 'Bekliyor',
  reserved: 'Rezerve',
  checked_in: 'Check-in',
  in_house: 'Konaklıyor',
  checked_out: 'Check-out',
  cancelled: 'İptal',
};

const STATUS_VARIANTS: Record<BoardingStayStatus | 'not_started', StatusVariant> = {
  not_started: 'pending',
  reserved: 'info',
  checked_in: 'warning',
  in_house: 'success',
  checked_out: 'neutral',
  cancelled: 'danger',
};

interface UnitForm {
  code: string;
  displayName: string;
  unitType: BoardingUnitType;
  status: BoardingUnitStatus;
  speciesAllowed: string;
  capacityPets: string;
  notes: string;
}

interface StayUpdateForm {
  message: string;
  visibility: BoardingUpdateVisibility;
  mood: BoardingUpdateMood;
}

const DEFAULT_UNIT_FORM: UnitForm = {
  code: '',
  displayName: '',
  unitType: 'kennel',
  status: 'active',
  speciesAllowed: 'dog, cat',
  capacityPets: '1',
  notes: '',
};

const DEFAULT_STAY_UPDATE_FORM: StayUpdateForm = {
  message: '',
  visibility: 'internal',
  mood: 'normal',
};

export function BoardingPage(): JSX.Element {
  const branch = useCurrentBranch();
  const branchId = branch?.branchId;
  const stays = useBoardingStays(branchId);
  const units = useBoardingUnits(branchId);
  const ensureStay = useEnsureBoardingStay(branchId);
  const upsertUnit = useUpsertBoardingUnit(branchId);
  const updateUnitStatus = useUpdateBoardingUnitStatus(branchId);
  const updateStay = useUpdateBoardingStay(branchId);
  const addStayUpdate = useAddBoardingStayUpdate(branchId);
  const createCashierOrder = useCreateBoardingCashierOrder(branchId);
  const bindQuote = useBindBoardingStayQuote(branchId);

  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [careNotes, setCareNotes] = useState('');
  const [unitForm, setUnitForm] = useState<UnitForm>(DEFAULT_UNIT_FORM);
  const [stayUpdateForm, setStayUpdateForm] = useState<StayUpdateForm>(DEFAULT_STAY_UPDATE_FORM);

  const items = stays.data ?? [];
  const unitItems = units.data ?? [];
  const selected = useMemo(
    () => items.find((item) => item.bookingId === selectedBookingId) ?? items[0] ?? null,
    [items, selectedBookingId],
  );
  const customerReport = useBoardingCustomerReport(selected?.stayId ?? undefined);

  const summary = useMemo(() => {
    const active = items.filter((item) => item.status === 'checked_in' || item.status === 'in_house').length;
    const reserved = items.filter((item) => item.status === 'reserved' || !item.stayId).length;
    const attention = items.filter((item) => item.occupancyAttention !== 'normal').length;
    const availableUnits = unitItems.filter((unit) => unit.status === 'active' && unit.activeStayCount < unit.capacityPets).length;
    return { active, reserved, attention, availableUnits };
  }, [items, unitItems]);

  const handleEnsureStay = async () => {
    if (!selected?.bookingId) return;
    try {
      await ensureStay.mutateAsync({
        bookingId: selected.bookingId,
        input: {
          unitId: selectedUnitId || null,
          careNotes: careNotes || null,
        },
      });
      toast.success('Boarding stay açıldı');
    } catch {
      toast.error('Boarding stay açılamadı');
    }
  };

  const handleStatus = async (status: BoardingStayStatus) => {
    if (!selected?.stayId) return;
    try {
      await updateStay.mutateAsync({
        stayId: selected.stayId,
        input: {
          status,
          unitId: selectedUnitId || undefined,
          careNotes: careNotes || undefined,
        },
      });
      toast.success(`Boarding durumu güncellendi: ${STATUS_LABELS[status]}`);
    } catch {
      toast.error('Boarding durumu güncellenemedi');
    }
  };

  const handleSaveUnit = async () => {
    if (!unitForm.code.trim() || !unitForm.displayName.trim()) return;
    try {
      await upsertUnit.mutateAsync({
        code: unitForm.code,
        displayName: unitForm.displayName,
        unitType: unitForm.unitType,
        status: unitForm.status,
        speciesAllowed: splitCsv(unitForm.speciesAllowed),
        capacityPets: Number(unitForm.capacityPets),
        notes: unitForm.notes || null,
      });
      setUnitForm(DEFAULT_UNIT_FORM);
      toast.success('Boarding unit kaydedildi');
    } catch {
      toast.error('Boarding unit kaydedilemedi');
    }
  };

  const handleUnitStatus = async (unitId: string, status: BoardingUnitStatus) => {
    try {
      await updateUnitStatus.mutateAsync({ unitId, status });
      toast.success('Unit durumu güncellendi');
    } catch {
      toast.error('Unit durumu güncellenemedi');
    }
  };

  const handleAddStayUpdate = async () => {
    if (!selected?.stayId || !stayUpdateForm.message.trim()) return;
    try {
      await addStayUpdate.mutateAsync({
        stayId: selected.stayId,
        input: stayUpdateForm,
      });
      setStayUpdateForm(DEFAULT_STAY_UPDATE_FORM);
      toast.success('Boarding update eklendi');
    } catch {
      toast.error('Boarding update eklenemedi');
    }
  };

  const handleCashierHandoff = async () => {
    if (!selected?.stayId) return;
    try {
      await createCashierOrder.mutateAsync({ stayId: selected.stayId });
      toast.success('Boarding kasaya aktarıldı');
    } catch {
      toast.error('Boarding kasaya aktarılamadı. Açık kasa oturumunu kontrol edin.');
    }
  };

  const handleBindQuote = async () => {
    if (!selected?.stayId) return;
    try {
      await bindQuote.mutateAsync({ stayId: selected.stayId });
      toast.success('Pricing quote bağlandı');
    } catch {
      toast.error('Pricing quote bağlanamadı');
    }
  };

  return (
    <div className="px-biz-card-lg py-biz-content-lg max-w-[1440px] mx-auto flex flex-col gap-biz-content-lg">
      <header className="rounded-biz-card border border-biz-border bg-gradient-to-br from-biz-surface to-biz-muted/40 p-biz-card-lg flex items-start sm:items-center justify-between gap-biz-content-lg flex-wrap">
        <div className="flex items-start gap-biz-content-lg min-w-0">
          <div className="h-12 w-12 rounded-biz-btn bg-biz-primary text-biz-primary-foreground flex items-center justify-center shrink-0">
            <Bed size={24} weight="fill" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-biz-2xl font-bold biz-heading">Boarding Hotel</h1>
            <p className="text-biz-sm text-biz-muted-foreground mt-1">
              Unit inventory, check-in/out state and occupancy readiness for hotel operations.
            </p>
          </div>
        </div>
        <StatusBadge variant={summary.attention > 0 ? 'warning' : 'success'} label={`${summary.attention} dikkat`} />
      </header>

      <section className="grid grid-cols-2 xl:grid-cols-4 gap-biz-content">
        <Metric label="Konaklayan" value={summary.active} icon={<HouseLine size={20} />} />
        <Metric label="Rezervasyon" value={summary.reserved} icon={<CalendarCheck size={20} />} />
        <Metric label="Boş unit" value={summary.availableUnits} icon={<DoorOpen size={20} />} />
        <Metric label="Dikkat" value={summary.attention} icon={<WarningCircle size={20} />} />
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-biz-content-lg">
        <Card className="overflow-hidden">
          <CardHeader>
            <div>
              <h2 className="font-semibold biz-heading">Boarding kuyruğu</h2>
              <p className="text-biz-sm text-biz-muted-foreground">
                `biz_bookings` boarding hizmetleri stay state ile birleşir.
              </p>
            </div>
          </CardHeader>
          <div className="divide-y divide-biz-border">
            {stays.isLoading ? <QueueSkeleton /> : null}
            {!stays.isLoading && items.length === 0 ? (
              <div className="px-biz-card py-biz-card text-biz-sm text-biz-muted-foreground">
                Yakın dönem boarding rezervasyonu bulunamadı.
              </div>
            ) : null}
            {items.map((item) => {
              const key = item.status ?? 'not_started';
              return (
                <button
                  key={item.bookingId}
                  type="button"
                  onClick={() => {
                    setSelectedBookingId(item.bookingId);
                    setSelectedUnitId(item.unitId ?? '');
                  }}
                  className="w-full min-h-biz-touch text-left px-biz-card py-biz-content-lg hover:bg-biz-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring"
                >
                  <div className="flex items-start justify-between gap-biz-content">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{item.petName ?? 'Petsiz'} · {item.serviceName}</p>
                      <p className="text-biz-sm text-biz-muted-foreground truncate">
                        {item.customerName} · {item.nights} gece
                      </p>
                      <p className="text-biz-xs text-biz-muted-foreground mt-1">
                        {formatDateTime(item.checkInDueAt)} → {formatDateTime(item.checkOutDueAt)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <StatusBadge variant={STATUS_VARIANTS[key]} label={STATUS_LABELS[key]} />
                      {item.occupancyAttention !== 'normal' ? (
                        <StatusBadge
                          variant={item.occupancyAttention === 'overdue' ? 'danger' : 'warning'}
                          label={item.occupancyAttention === 'overdue' ? 'Gecikti' : 'Bugün'}
                        />
                      ) : null}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-biz-content-lg">
          <Card>
            <CardHeader>
              <div>
                <h2 className="font-semibold biz-heading">Stay operasyonu</h2>
                <p className="text-biz-sm text-biz-muted-foreground">
                  Unit atama, check-in/out ve temel bakım notları.
                </p>
              </div>
            </CardHeader>
            <CardBody className="flex flex-col gap-biz-content">
              <SelectedStay selected={selected} />
              <NightlyQuotePanel
                quote={selected?.nightlyQuote ?? null}
                loading={bindQuote.isPending}
                disabled={!selected?.stayId || selected.nightlyQuote.finalizerStatus === 'quote_bound'}
                onBindQuote={handleBindQuote}
              />
              <CustomerReportPreviewPanel report={customerReport.data ?? null} loading={customerReport.isLoading} />
              <CashierHandoffPanel
                disabled={!selected?.stayId}
                loading={createCashierOrder.isPending}
                onSubmit={handleCashierHandoff}
              />
              <StayUpdateComposer
                value={stayUpdateForm}
                loading={addStayUpdate.isPending}
                disabled={!selected?.stayId}
                onChange={setStayUpdateForm}
                onSubmit={handleAddStayUpdate}
              />
              <StayUpdateFeed events={selected?.updateEvents ?? []} />
              <label className="flex flex-col gap-1 text-biz-sm font-medium">
                <span>Unit</span>
                <select
                  value={selectedUnitId}
                  onChange={(event) => setSelectedUnitId(event.target.value)}
                  className="w-full min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
                >
                  <option value="">Unit seçilmedi</option>
                  {unitItems.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.displayName} · {unit.unitType} · {unit.activeStayCount}/{unit.capacityPets}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-biz-sm font-medium">
                <span>Bakım notu</span>
                <textarea
                  value={careNotes}
                  onChange={(event) => setCareNotes(event.target.value)}
                  className="w-full min-h-[96px] rounded-biz-input border border-biz-input bg-biz-background p-biz-content"
                  placeholder="Beslenme, ilaç, ayrılık kaygısı veya oda tercihi notu."
                />
              </label>
              <div className="flex gap-biz-content flex-wrap">
                <Button
                  type="button"
                  onClick={handleEnsureStay}
                  disabled={!selected?.bookingId}
                  loading={ensureStay.isPending}
                >
                  Stay aç / güncelle
                </Button>
                <Button
                  type="button"
                  intent="secondary"
                  onClick={() => handleStatus('checked_in')}
                  disabled={!selected?.stayId}
                  loading={updateStay.isPending}
                >
                  Check-in
                </Button>
                <Button
                  type="button"
                  intent="secondary"
                  onClick={() => handleStatus('in_house')}
                  disabled={!selected?.stayId}
                  loading={updateStay.isPending}
                >
                  Konaklıyor
                </Button>
                <Button
                  type="button"
                  intent="secondary"
                  onClick={() => handleStatus('checked_out')}
                  disabled={!selected?.stayId}
                  loading={updateStay.isPending}
                >
                  Check-out
                </Button>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <div>
                <h2 className="font-semibold biz-heading">Unit envanteri</h2>
                <p className="text-biz-sm text-biz-muted-foreground">
                  Faz 3 slice 1 foundation: occupancy state, maintenance visibility.
                </p>
              </div>
            </CardHeader>
            <CardBody className="flex flex-col gap-biz-content">
              <UnitManagementForm
                value={unitForm}
                loading={upsertUnit.isPending}
                onChange={setUnitForm}
                onSubmit={handleSaveUnit}
              />
              <UnitHeatmapPanel units={unitItems} />
              {unitItems.length === 0 ? (
                <div className="rounded-biz-card border border-dashed border-biz-border px-biz-content-lg py-biz-content text-biz-sm text-biz-muted-foreground">
                  Henüz boarding unit kaydı yok. Unit yönetimi formundan ilk oda/kennel kaydını açabilirsiniz.
                </div>
              ) : null}
              {unitItems.map((unit) => (
                <div key={unit.id} className="rounded-biz-card border border-biz-border bg-biz-background px-biz-content-lg py-biz-content">
                  <div className="flex items-start justify-between gap-biz-content">
                    <div>
                      <p className="font-semibold">{unit.displayName}</p>
                      <p className="text-biz-xs text-biz-muted-foreground">
                        {unit.code} · {unit.unitType} · {unit.speciesAllowed.join(', ')}
                      </p>
                    </div>
                    <StatusBadge
                      variant={unit.status === 'active' ? 'success' : unit.status === 'maintenance' ? 'warning' : 'neutral'}
                      label={`${unit.activeStayCount}/${unit.capacityPets}`}
                    />
                  </div>
                  <div className="mt-biz-content flex gap-2 flex-wrap">
                    <Button
                      type="button"
                      intent="secondary"
                      disabled={unit.status === 'active'}
                      loading={updateUnitStatus.isPending}
                      onClick={() => handleUnitStatus(unit.id, 'active')}
                    >
                      Aktif
                    </Button>
                    <Button
                      type="button"
                      intent="secondary"
                      disabled={unit.status === 'maintenance'}
                      loading={updateUnitStatus.isPending}
                      onClick={() => handleUnitStatus(unit.id, 'maintenance')}
                    >
                      Bakım
                    </Button>
                    <Button
                      type="button"
                      intent="secondary"
                      disabled={unit.status === 'inactive'}
                      loading={updateUnitStatus.isPending}
                      onClick={() => handleUnitStatus(unit.id, 'inactive')}
                    >
                      Pasif
                    </Button>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value, icon }: { label: string; value: number; icon: JSX.Element }): JSX.Element {
  return (
    <Card>
      <CardBody className="flex items-center justify-between gap-biz-content">
        <div>
          <p className="text-biz-xs text-biz-muted-foreground">{label}</p>
          <p className="text-biz-2xl font-bold biz-heading">{value}</p>
        </div>
        <div className="h-10 w-10 rounded-biz-btn bg-biz-muted text-biz-muted-foreground flex items-center justify-center">
          {icon}
        </div>
      </CardBody>
    </Card>
  );
}

function SelectedStay({
  selected,
}: {
  selected: {
    updateEvents: readonly BoardingStayUpdateEvent[];
    petName: string | null;
    customerName: string;
    serviceName: string;
    checkInDueAt: string;
    checkOutDueAt: string;
    unitName: string | null;
  } | null;
}): JSX.Element {
  if (!selected) {
    return (
      <div className="rounded-biz-card border border-biz-border bg-biz-muted/30 px-biz-content-lg py-biz-content text-biz-sm text-biz-muted-foreground">
        Soldan bir boarding rezervasyonu seçin.
      </div>
    );
  }
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-muted/30 px-biz-content-lg py-biz-content">
      <p className="font-semibold">{selected.petName ?? 'Petsiz'} · {selected.serviceName}</p>
      <p className="text-biz-sm text-biz-muted-foreground">
        {selected.customerName} · {selected.unitName ?? 'Unit atanmamış'}
      </p>
      <p className="text-biz-xs text-biz-muted-foreground mt-1">
        {formatDateTime(selected.checkInDueAt)} → {formatDateTime(selected.checkOutDueAt)}
      </p>
    </div>
  );
}

function NightlyQuotePanel({
  quote,
  loading,
  disabled,
  onBindQuote,
}: {
  quote: BoardingNightlyQuotePreview | null;
  loading: boolean;
  disabled: boolean;
  onBindQuote: () => void;
}): JSX.Element {
  if (!quote) {
    return (
      <div className="rounded-biz-card border border-biz-border bg-biz-muted/20 px-biz-content-lg py-biz-content text-biz-sm text-biz-muted-foreground">
        Nightly quote için önce bir boarding rezervasyonu seçin.
      </div>
    );
  }
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-background px-biz-content-lg py-biz-content">
      <div className="flex items-start justify-between gap-biz-content">
        <div>
          <p className="font-semibold biz-heading">Nightly quote preview</p>
          <p className="text-biz-xs text-biz-muted-foreground mt-1">
            {quote.nights} gece × {formatMoneyMinor(quote.unitBasePriceMinor, quote.currency)}
          </p>
        </div>
        <StatusBadge
          variant={quote.finalizerStatus === 'quote_bound' ? 'success' : 'warning'}
          label={quote.finalizerStatus === 'quote_bound' ? 'quote bound' : 'preview'}
        />
      </div>
      <div className="mt-biz-content grid grid-cols-3 gap-biz-content text-biz-sm">
        <QuoteCell label="Baz" value={formatMoneyMinor(quote.baseAmountMinor, quote.currency)} />
        <QuoteCell label={`KDV %${quote.taxRateBp / 100}`} value={formatMoneyMinor(quote.taxAmountMinor, quote.currency)} />
        <QuoteCell label="Toplam" value={formatMoneyMinor(quote.totalAmountMinor, quote.currency)} strong />
      </div>
      <p className="mt-biz-content text-biz-xs text-biz-muted-foreground">
        Contract: {quote.pricingContract}
        {quote.quoteId ? ` · quote ${quote.quoteId.slice(0, 8)} · ${quote.quoteState}` : ''}
      </p>
      <div className="mt-biz-content flex justify-end">
        <Button
          type="button"
          intent="secondary"
          disabled={disabled}
          loading={loading}
          onClick={onBindQuote}
        >
          PricingFinalizer quote bağla
        </Button>
      </div>
    </div>
  );
}

function CustomerReportPreviewPanel({
  report,
  loading,
}: {
  report: BoardingCustomerReport | null;
  loading: boolean;
}): JSX.Element {
  if (loading) {
    return (
      <div className="rounded-biz-card border border-biz-border bg-biz-muted/20 px-biz-content-lg py-biz-content text-biz-sm text-biz-muted-foreground">
        Müşteri raporu hazırlanıyor...
      </div>
    );
  }
  if (!report) {
    return (
      <div className="rounded-biz-card border border-biz-border bg-biz-muted/20 px-biz-content-lg py-biz-content text-biz-sm text-biz-muted-foreground">
        Müşteri raporu için stay açın ve soldan seçin.
      </div>
    );
  }
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-background px-biz-content-lg py-biz-content">
      <div className="flex items-start justify-between gap-biz-content">
        <div>
          <p className="font-semibold biz-heading">Müşteri raporu önizlemesi</p>
          <p className="text-biz-xs text-biz-muted-foreground mt-1">
            {report.petName ?? 'Pet'} · {report.nights} gece · {report.unitName ?? 'Unit atanmamış'}
          </p>
        </div>
        <StatusBadge
          variant={report.publishReady ? 'success' : 'warning'}
          label={report.publishReady ? 'Hazır' : `${report.missingFields.length} eksik`}
        />
      </div>
      <div className="mt-biz-content rounded-biz-card bg-biz-muted/30 p-biz-content">
        <p className="text-biz-sm font-medium">{report.careNotes ?? 'Bakım notu henüz girilmedi.'}</p>
        <p className="mt-1 text-biz-xs text-biz-muted-foreground">
          {formatDateTime(report.checkInDueAt)} → {formatDateTime(report.checkOutDueAt)}
        </p>
      </div>
      <div className="mt-biz-content flex flex-col gap-2">
        {report.customerUpdates.length === 0 ? (
          <p className="text-biz-sm text-biz-muted-foreground">Müşteriye açık update yok.</p>
        ) : report.customerUpdates.slice(0, 3).map((event) => (
          <div key={event.id} className="rounded-biz-card border border-biz-border bg-biz-muted/20 p-biz-content">
            <p className="text-biz-sm font-medium">{event.message}</p>
            <p className="mt-1 text-biz-xs text-biz-muted-foreground">{formatDateTime(event.createdAt)} · {event.mood}</p>
          </div>
        ))}
      </div>
      {report.missingFields.length > 0 ? (
        <p className="mt-biz-content text-biz-xs text-biz-muted-foreground">
          Eksik: {report.missingFields.join(', ')}
        </p>
      ) : null}
    </div>
  );
}

function CashierHandoffPanel({
  disabled,
  loading,
  onSubmit,
}: {
  disabled: boolean;
  loading: boolean;
  onSubmit: () => void;
}): JSX.Element {
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-muted/20 px-biz-content-lg py-biz-content">
      <div className="flex items-start justify-between gap-biz-content flex-wrap">
        <div>
          <p className="font-semibold biz-heading">Kasaya aktar</p>
          <p className="text-biz-xs text-biz-muted-foreground mt-1">
            Stay için nightly satırı aktif cashier oturumuna idempotent olarak eklenir.
          </p>
        </div>
        <Button
          type="button"
          intent="secondary"
          disabled={disabled}
          loading={loading}
          onClick={onSubmit}
        >
          Kasaya aktar
        </Button>
      </div>
    </div>
  );
}

function QuoteCell({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}): JSX.Element {
  return (
    <div className="rounded-biz-card bg-biz-muted/30 p-biz-content">
      <p className="text-biz-xs text-biz-muted-foreground">{label}</p>
      <p className={strong ? 'font-bold biz-heading' : 'font-semibold'}>
        {value}
      </p>
    </div>
  );
}

function StayUpdateComposer({
  value,
  loading,
  disabled,
  onChange,
  onSubmit,
}: {
  value: StayUpdateForm;
  loading: boolean;
  disabled: boolean;
  onChange: (value: StayUpdateForm) => void;
  onSubmit: () => void;
}): JSX.Element {
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-muted/20 p-biz-content-lg">
      <p className="font-semibold biz-heading">Boarding update</p>
      <p className="text-biz-xs text-biz-muted-foreground mt-1">
        İç ekip notu veya müşteriye gidecek kısa bakım güncellemesi.
      </p>
      <textarea
        value={value.message}
        onChange={(event) => onChange({ ...value, message: event.target.value })}
        className="mt-biz-content w-full min-h-[88px] rounded-biz-input border border-biz-input bg-biz-background p-biz-content"
        placeholder="Örn. Mama yedi, kısa yürüyüş yaptı, sakinleşti."
        disabled={disabled}
      />
      <div className="mt-biz-content grid grid-cols-1 sm:grid-cols-2 gap-biz-content">
        <select
          value={value.visibility}
          onChange={(event) => onChange({ ...value, visibility: event.target.value as BoardingUpdateVisibility })}
          className="min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
          disabled={disabled}
        >
          <option value="internal">İç not</option>
          <option value="customer">Müşteri update</option>
        </select>
        <select
          value={value.mood}
          onChange={(event) => onChange({ ...value, mood: event.target.value as BoardingUpdateMood })}
          className="min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
          disabled={disabled}
        >
          <option value="normal">Normal</option>
          <option value="happy">Mutlu</option>
          <option value="anxious">Kaygılı</option>
          <option value="medical">Medikal</option>
          <option value="incident">Olay</option>
        </select>
      </div>
      <div className="mt-biz-content flex justify-end">
        <Button
          type="button"
          intent="secondary"
          disabled={disabled || !value.message.trim()}
          loading={loading}
          onClick={onSubmit}
        >
          Update ekle
        </Button>
      </div>
    </div>
  );
}

function StayUpdateFeed({
  events,
}: {
  events: readonly BoardingStayUpdateEvent[];
}): JSX.Element {
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-background px-biz-content-lg py-biz-content">
      <div className="flex items-center justify-between gap-biz-content">
        <p className="font-semibold biz-heading">Stay timeline</p>
        <StatusBadge variant="info" label={`${events.length} update`} />
      </div>
      {events.length === 0 ? (
        <p className="mt-biz-content text-biz-sm text-biz-muted-foreground">
          Henüz boarding update yok.
        </p>
      ) : (
        <div className="mt-biz-content flex flex-col gap-biz-content">
          {events.slice(0, 6).map((event) => (
            <div key={event.id} className="rounded-biz-card border border-biz-border bg-biz-muted/20 p-biz-content">
              <div className="flex items-start justify-between gap-biz-content">
                <p className="text-biz-sm font-medium">{event.message}</p>
                <StatusBadge
                  variant={event.visibility === 'customer' ? 'success' : 'neutral'}
                  label={event.visibility === 'customer' ? 'Müşteri' : 'İç'}
                />
              </div>
              <p className="mt-1 text-biz-xs text-biz-muted-foreground">
                {formatDateTime(event.createdAt)} · {event.mood}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UnitManagementForm({
  value,
  loading,
  onChange,
  onSubmit,
}: {
  value: UnitForm;
  loading: boolean;
  onChange: (value: UnitForm) => void;
  onSubmit: () => void;
}): JSX.Element {
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-muted/20 p-biz-content-lg">
      <p className="font-semibold biz-heading">Unit yönetimi</p>
      <p className="text-biz-xs text-biz-muted-foreground mt-1">
        Oda/kennel/suite kaydı oluşturur veya aynı kodla günceller.
      </p>
      <div className="mt-biz-content grid grid-cols-1 sm:grid-cols-2 gap-biz-content">
        <input
          value={value.code}
          onChange={(event) => onChange({ ...value, code: event.target.value })}
          className="min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
          placeholder="Kod: K-01"
        />
        <input
          value={value.displayName}
          onChange={(event) => onChange({ ...value, displayName: event.target.value })}
          className="min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
          placeholder="Görünen ad: Kennel 01"
        />
        <select
          value={value.unitType}
          onChange={(event) => onChange({ ...value, unitType: event.target.value as BoardingUnitType })}
          className="min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
        >
          <option value="kennel">Kennel</option>
          <option value="suite">Suite</option>
          <option value="room">Room</option>
          <option value="cat_condo">Cat condo</option>
          <option value="isolation">Isolation</option>
        </select>
        <select
          value={value.status}
          onChange={(event) => onChange({ ...value, status: event.target.value as BoardingUnitStatus })}
          className="min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
        >
          <option value="active">Aktif</option>
          <option value="maintenance">Bakım</option>
          <option value="inactive">Pasif</option>
        </select>
        <input
          value={value.speciesAllowed}
          onChange={(event) => onChange({ ...value, speciesAllowed: event.target.value })}
          className="min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
          placeholder="dog, cat"
        />
        <input
          value={value.capacityPets}
          onChange={(event) => onChange({ ...value, capacityPets: event.target.value })}
          type="number"
          min="1"
          max="8"
          className="min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
          placeholder="Kapasite"
        />
      </div>
      <textarea
        value={value.notes}
        onChange={(event) => onChange({ ...value, notes: event.target.value })}
        className="mt-biz-content w-full min-h-[72px] rounded-biz-input border border-biz-input bg-biz-background p-biz-content"
        placeholder="Bakım, izolasyon veya kullanım notu."
      />
      <div className="mt-biz-content flex justify-end">
        <Button
          type="button"
          intent="secondary"
          disabled={!value.code.trim() || !value.displayName.trim()}
          loading={loading}
          onClick={onSubmit}
        >
          Unit kaydet
        </Button>
      </div>
    </div>
  );
}

function UnitHeatmapPanel({
  units,
}: {
  units: Array<{
    id: string;
    displayName: string;
    status: BoardingUnitStatus;
    activeStayCount: number;
    capacityPets: number;
  }>;
}): JSX.Element {
  if (units.length === 0) {
    return (
      <div className="rounded-biz-card border border-biz-border bg-biz-muted/20 px-biz-content-lg py-biz-content text-biz-sm text-biz-muted-foreground">
        Occupancy heatmap unit kaydı geldikçe dolacak.
      </div>
    );
  }
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-muted/20 p-biz-content-lg">
      <div className="flex items-center justify-between gap-biz-content">
        <div>
          <p className="font-semibold biz-heading">Occupancy heatmap</p>
          <p className="text-biz-xs text-biz-muted-foreground mt-1">
            Doluluk, bakım ve kapasite aşımı riskini hızlı gösterir.
          </p>
        </div>
        <StatusBadge variant="info" label={`${units.length} unit`} />
      </div>
      <div className="mt-biz-content grid grid-cols-1 sm:grid-cols-2 gap-biz-content">
        {units.map((unit) => {
          const ratio = Math.min(100, Math.round((unit.activeStayCount / Math.max(1, unit.capacityPets)) * 100));
          const variant: StatusVariant = unit.status !== 'active'
            ? 'neutral'
            : ratio >= 100
              ? 'danger'
              : ratio >= 75
                ? 'warning'
                : 'success';
          return (
            <div key={unit.id} className="rounded-biz-card border border-biz-border bg-biz-background p-biz-content">
              <div className="flex items-center justify-between gap-biz-content">
                <p className="text-biz-sm font-semibold truncate">{unit.displayName}</p>
                <StatusBadge variant={variant} label={unit.status === 'active' ? `${ratio}%` : unit.status} />
              </div>
              <div className="mt-2 h-2 rounded-biz-full bg-biz-muted overflow-hidden">
                <div
                  className={[
                    'h-full rounded-biz-full',
                    variant === 'danger' ? 'bg-biz-danger' : variant === 'warning' ? 'bg-biz-warning' : 'bg-biz-success',
                  ].join(' ')}
                  style={{ width: `${unit.status === 'active' ? ratio : 100}%` }}
                />
              </div>
              <p className="mt-2 text-biz-xs text-biz-muted-foreground">
                {unit.activeStayCount}/{unit.capacityPets} aktif stay
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QueueSkeleton(): JSX.Element {
  return (
    <>
      {[0, 1, 2].map((idx) => (
        <div key={idx} className="px-biz-card py-biz-content-lg animate-pulse">
          <div className="h-4 w-2/3 rounded bg-biz-muted" />
          <div className="mt-2 h-3 w-1/2 rounded bg-biz-muted" />
        </div>
      ))}
    </>
  );
}

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatMoneyMinor(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency,
  }).format(amountMinor / 100);
}
